import { Calendar, MapPin, DollarSign, Users, Eye, Edit, X, Check, Clock, Shield } from "lucide-react";
import { jobStatus, badgeStyle } from "@/utils/szStatus";

const JOB_TYPE = {
  WEDDINGS:    { label: 'Wedding',     fg: '#BE185D', bg: '#FCE7F3' },
  CORPORATE:   { label: 'Corporate',   fg: '#2563EB', bg: '#DBEAFE' },
  EVENTS:      { label: 'Events',      fg: '#C2410C', bg: '#FFEDD5' },
  REAL_ESTATE: { label: 'Real Estate', fg: '#0F766E', bg: '#CCFBF1' },
  OTHERS:      { label: 'Other',       fg: '#475569', bg: '#E2E8F0' },
};

const AdminJobViewModal = ({ job, isOpen, onClose, onApprove, onEdit, onCancel }) => {
  if (!isOpen || !job) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Extract all dates from dateAssignments first, then fall back to job.dates
  const getAllDates = () => {
    const crewMembers = job.crew_members || job.crewMembers || [];
    if (crewMembers.length > 0 && crewMembers.some(m => m.dateAssignments && m.dateAssignments.length > 0)) {
      const allDates = new Set();
      crewMembers.forEach(member => {
        if (member.dateAssignments) {
          member.dateAssignments.forEach(a => {
            if (a.date) allDates.add(a.date.split('T')[0]);
          });
        }
      });
      if (allDates.size > 0) return Array.from(allDates).sort();
    }
    if (job.additional_dates && job.additional_dates.length > 0) {
      return [job.date, ...job.additional_dates].filter(Boolean).map(d => d.split('T')[0]);
    }
    return job.dates || (job.date ? [job.date.split('T')[0]] : []);
  };

  const allDates = getAllDates();
  const crewMembers = job.crew_members || job.crewMembers || [];

  // Calculate total hours across all crew members
  const totalHours = crewMembers.reduce((total, member) => {
    if (member.dateAssignments && member.dateAssignments.length > 0) {
      return total + member.dateAssignments.reduce((sum, a) => sum + (parseFloat(a.hours) || 0), 0);
    }
    return total + (parseFloat(member.hours) || 0);
  }, 0);

  const jobType = JOB_TYPE[job.job_type || job.jobType] || null;
  const status = jobStatus(job.status || 'PENDING_APPROVAL');
  const jobId = job.job_number || `JOB-${job.id?.slice(0, 6).toUpperCase()}`;

  const memberSubtotal = (member) => member.subtotal || (
    member.dateAssignments
      ? member.dateAssignments.reduce((sum, a) => sum + ((parseFloat(a.hours) || 0) * (parseFloat(a.ratePerHour) || 0)), 0)
      : (parseFloat(member.hours) || 0) * (parseFloat(member.ratePerHour) || 0)
  );

  const SectionTitle = ({ icon: Icon, children }) => (
    <div className="flex items-center gap-2.5 mb-3">
      <Icon className="w-[18px] h-[18px] text-[#572B7C]" />
      <h3 className="sz-h3" style={{ margin: 0 }}>{children}</h3>
    </div>
  );

  return (
    <div
      className="sz-modal-backdrop"
      style={{ zIndex: 150 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="sz-modal-card szscroll"
        style={{ width: '100%', maxWidth: '880px', overflow: 'hidden' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Branded header */}
        <div
          className="sz-modal-header-grad"
          style={{ flexShrink: 0, padding: '22px 26px', position: 'relative' }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/70 hover:text-white hover:bg-white/15 rounded-lg p-1.5 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex flex-wrap items-center gap-2 mb-2.5" style={{ paddingRight: '40px' }}>
            <span className="sz-mono" style={{ fontSize: '11px', fontWeight: 600, color: '#fff', background: 'rgba(255,255,255,.18)', padding: '4px 10px', borderRadius: '6px' }}>
              {jobId}
            </span>
            <span className="sz-badge sz-nodot" style={{ color: status.fg, background: status.bg }}>{status.label}</span>
            {jobType && (
              <span style={{ fontSize: '11px', fontWeight: 600, fontFamily: 'Poppins', color: jobType.fg, background: jobType.bg, padding: '4px 10px', borderRadius: '6px' }}>
                {jobType.label}
              </span>
            )}
            {job.requiresWWCC && (
              <span className="inline-flex items-center gap-1" style={{ fontSize: '11px', fontWeight: 600, fontFamily: 'Poppins', color: '#B45309', background: '#FEF3C7', padding: '4px 10px', borderRadius: '6px' }}>
                <Shield className="w-3.5 h-3.5" /> WWCC Required
              </span>
            )}
          </div>
          <h2 style={{ fontFamily: 'Poppins', fontWeight: 700, fontSize: '22px', letterSpacing: '-.4px', color: '#fff', margin: 0 }}>
            {job.title}
          </h2>
          <div className="flex items-center gap-2 mt-2" style={{ color: 'rgba(255,255,255,.85)' }}>
            <Calendar className="w-[18px] h-[18px]" />
            <span style={{ fontSize: '13px', fontWeight: 600 }}>
              {allDates.length} day{allDates.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="szscroll" style={{ flex: 1, overflowY: 'auto', padding: '22px 26px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Quick stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { icon: Users, label: 'Roles', value: `${crewMembers.length} role${crewMembers.length !== 1 ? 's' : ''}` },
              { icon: Calendar, label: 'Job Days', value: `${allDates.length} day${allDates.length !== 1 ? 's' : ''}` },
              { icon: Clock, label: 'Total Hours', value: `${totalHours}h` },
            ].map((s, i) => (
              <div key={i} className="sz-card" style={{ padding: '14px 16px' }}>
                <s.icon className="w-[18px] h-[18px] text-[#572B7C] mb-1.5" />
                <p style={{ fontSize: '11px', color: '#8A90A2', fontWeight: 600, margin: 0 }}>{s.label}</p>
                <p className="sz-stat-num" style={{ fontSize: '17px', margin: '3px 0 0' }}>{s.value}</p>
              </div>
            ))}
            <div className="sz-card" style={{ padding: '14px 16px', background: 'linear-gradient(135deg,#EDE4F4,#F7E8F4)', borderColor: '#E6D4EC' }}>
              <DollarSign className="w-[18px] h-[18px] text-[#78287C] mb-1.5" />
              <p style={{ fontSize: '11px', color: '#572B7C', fontWeight: 600, margin: 0 }}>Total Budget</p>
              <p className="sz-stat-num" style={{ fontSize: '19px', margin: '3px 0 0', color: '#572B7C' }}>
                {formatCurrency(job.budget || job.total)}
              </p>
            </div>
          </div>

          {/* Event Dates */}
          <div className="sz-card" style={{ padding: '18px' }}>
            <SectionTitle icon={Calendar}>Event Dates</SectionTitle>
            <div className="flex flex-wrap gap-2">
              {allDates.map((date, idx) => (
                <span key={idx} style={{ fontSize: '12px', fontWeight: 600, fontFamily: 'Poppins', color: '#2563EB', background: '#DBEAFE', padding: '6px 12px', borderRadius: '8px' }}>
                  {formatDate(date)}
                </span>
              ))}
            </div>
          </div>

          {/* Location */}
          <div className="sz-card" style={{ padding: '18px' }}>
            <SectionTitle icon={MapPin}>Location</SectionTitle>
            <p style={{ color: '#2B3142', fontSize: '13.5px', margin: 0 }}>
              {job.location || job.location_address || 'Location not specified'}
            </p>
          </div>

          {/* Crew Breakdown with Per-Date Assignments */}
          {crewMembers.length > 0 && (
            <div className="sz-card" style={{ padding: '18px' }}>
              <SectionTitle icon={Users}>
                Crew Breakdown ({crewMembers.length} role{crewMembers.length !== 1 ? 's' : ''})
              </SectionTitle>
              <div className="flex flex-col gap-3">
                {crewMembers.map((member, idx) => {
                  const subtotal = memberSubtotal(member);
                  return (
                    <div key={idx} style={{ border: '1px solid #E6E9EF', borderRadius: '12px', padding: '14px', background: '#FAFBFC' }}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div
                            className="flex items-center justify-center text-white"
                            style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'linear-gradient(120deg,#572B7C,#78287C)', fontFamily: 'Poppins', fontWeight: 700, fontSize: '14px' }}
                          >
                            {idx + 1}
                          </div>
                          <h4 style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: '14px', textTransform: 'capitalize', margin: 0 }}>
                            {(member.role || 'Crew Member').replace(/_/g, ' ')}
                          </h4>
                        </div>
                        <p style={{ fontFamily: 'Poppins', fontWeight: 700, fontSize: '17px', color: '#572B7C', margin: 0 }}>
                          ${subtotal.toLocaleString()}
                        </p>
                      </div>

                      {member.dateAssignments && member.dateAssignments.length > 0 ? (
                        <div className="flex flex-col gap-2">
                          {member.dateAssignments.map((assignment, aIdx) => {
                            const hours = parseFloat(assignment.hours) || 0;
                            const rate = parseFloat(assignment.ratePerHour) || 0;
                            const sub = hours * rate;
                            return (
                              <div key={aIdx} className="flex items-center justify-between" style={{ background: '#fff', border: '1px solid #F1F3F7', borderRadius: '9px', padding: '7px 11px' }}>
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-3.5 h-3.5 text-[#A8AEBC]" />
                                  <span style={{ fontSize: '12.5px', fontWeight: 500, color: '#2B3142' }}>{formatDate(assignment.date)}</span>
                                </div>
                                <div className="flex items-center gap-4" style={{ fontSize: '12.5px', color: '#8A90A2' }}>
                                  <span>{hours}h</span>
                                  <span>@ ${rate}/hr</span>
                                  <span style={{ fontWeight: 700, color: '#0C1120' }}>${sub.toLocaleString()}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { k: 'Duration', v: `${member.hours || 0}h` },
                            { k: 'Rate', v: `$${member.ratePerHour || 0}/hr` },
                            { k: 'Subtotal', v: `$${subtotal.toLocaleString()}` },
                          ].map((c) => (
                            <div key={c.k} style={{ background: '#fff', border: '1px solid #F1F3F7', borderRadius: '9px', padding: '8px', textAlign: 'center' }}>
                              <p style={{ fontSize: '11px', color: '#8A90A2', margin: 0 }}>{c.k}</p>
                              <p style={{ fontFamily: 'Poppins', fontWeight: 700, fontSize: '13.5px', margin: '2px 0 0' }}>{c.v}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Description */}
          {(job.description || job.brief) && (
            <div className="sz-card" style={{ padding: '18px' }}>
              <SectionTitle icon={Eye}>Job Description</SectionTitle>
              <p style={{ color: '#2B3142', fontSize: '13.5px', lineHeight: 1.6, whiteSpace: 'pre-wrap', margin: 0 }}>
                {job.description || job.brief}
              </p>
            </div>
          )}

          {/* Posted By */}
          {(job.client_name || job.owner_name) && (
            <div className="sz-card" style={{ padding: '18px' }}>
              <SectionTitle icon={Users}>Posted By</SectionTitle>
              <p style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: '14px', color: '#0C1120', margin: 0 }}>
                {job.client_name || job.owner_name}
              </p>
            </div>
          )}

          {/* Crew Earnings (After 2% Fee) */}
          <div className="sz-card" style={{ padding: '18px', background: '#F0FDF4', borderColor: '#BBF7D0' }}>
            <h3 className="sz-h3" style={{ margin: '0 0 14px' }}>Crew Earnings (After 2% Fee)</h3>
            <div className="flex flex-col gap-2.5">
              {crewMembers.map((member, idx) => {
                const crewPayout = Math.floor(memberSubtotal(member) * 0.98);
                return (
                  <div key={idx} className="flex justify-between items-center">
                    <span style={{ color: '#2B3142', fontSize: '13px', textTransform: 'capitalize' }}>
                      {(member.role || 'Role ' + (idx + 1)).replace(/_/g, ' ')}
                    </span>
                    <span style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: '13.5px', color: '#0C1120' }}>
                      ${crewPayout.toLocaleString()}
                    </span>
                  </div>
                );
              })}
              <div style={{ borderTop: '1px solid #BBF7D0', paddingTop: '12px' }}>
                <div className="flex justify-between items-center">
                  <span style={{ fontFamily: 'Poppins', fontWeight: 700, fontSize: '15px' }}>Total Crew Payout</span>
                  <span className="sz-stat-num" style={{ color: '#15803D' }}>
                    ${Math.floor((parseFloat(job.budget || job.total) || 0) * 0.98).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div style={{ flexShrink: 0, background: '#FAFBFC', borderTop: '1px solid #E6E9EF', padding: '14px 18px', display: 'flex', gap: '10px' }}>
          <button
            onClick={() => onApprove(job)}
            className="sz-btn sz-btn-primary"
            style={{ flex: 1, height: '44px' }}
          >
            <Check className="w-[17px] h-[17px]" strokeWidth={2.4} />
            Approve Job
          </button>
          <button
            onClick={() => onEdit(job)}
            className="sz-btn sz-btn-outline"
            style={{ flex: 1, height: '44px' }}
          >
            <Edit className="w-4 h-4" />
            Edit Job
          </button>
          <button
            onClick={() => {
              if (window.confirm(`Cancel job "${job.title}"? This cannot be undone.`)) {
                onCancel(job.id, 'Cancelled by admin');
              }
            }}
            className="sz-btn"
            style={{ flex: 1, height: '44px', background: '#fff', border: '1px solid #FECACA', color: '#DC2626' }}
          >
            <X className="w-4 h-4" strokeWidth={2.2} />
            Cancel Job
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminJobViewModal;
